import * as React from 'react';
import { rem } from 'polished';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import { styled } from '../themeContext';
import { ReactComponent as CloseIconSvg } from './dimmedPopup/close.svg';

interface PopupProps {
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  onCloseButtonClick?: () => void;
  hide?: boolean;
  children?: React.ReactElement;
}

const ContentBox = styled.div({
  padding: `0`,
  margin: '0 auto',
  background: 'rgba(25,29,36,0.95)',
});
interface DimmedPopupContainerProps {
  hide: boolean;
}

const DimmedPopupContainer = styled.div<DimmedPopupContainerProps>(props => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100vw',
  height: '100vh',
  minHeight: '100vh',
  transform: props.hide ? 'translateX(100%)' : 'none',
  pointerEvents: props.hide ? 'none' : undefined,
  transition: 'transform .5s',
  zIndex: 1,
}));

const Dim = styled.div({
  position: 'absolute',
  width: '100%',
  height: '100%',
  background: 'dim',
});

const Contents = styled.div({
  position: 'absolute',
  width: '100%',
  height: '100%',
  overflowX: 'hidden',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  color: '#fff',
});

const CloseIcon = styled(CloseIconSvg)((props) => ({
  fontSize: rem(25),
  [props.theme.media['sm']]: {
    fontSize: rem(35),
  },
  [props.theme.media['md']]: {
    fontSize: rem(45),
  },
}));

const CloseButton = styled.button({
  position: 'absolute',
  top: rem(30),
  cursor: 'pointer',
  right: rem(30),
  padding: 0,
  color: '#fff',
  background: 'none',
  border: 'none',
  outline: 'none',
});

const DimmedPopup: React.FC<PopupProps> = ({
  id,
  className,
  style,
  children,
  onCloseButtonClick,
  hide,
}) => {
  const domRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const dom = domRef.current;
    if (!dom) return;
    if (hide) {
      enableBodyScroll(dom);
    } else {
      disableBodyScroll(dom, {
        allowTouchMove: el => {
          while (el && el !== document.body) {
            if (el.getAttribute('data-scroll-lock-ignore') !== null) {
              return true;
            }
            el = el.parentElement!;
          }
          return false;
        },
      });
    }
    return () => enableBodyScroll(dom);
  }, [hide]);
  return (
    <DimmedPopupContainer
      id={id}
      style={style}
      className={className}
      hide={!!hide}
    >
      <Dim />
      <Contents>
        <ContentBox css={{
          position: 'relative',
          minHeight: '100%',
        }}>
          <CloseButton
            onClick={onCloseButtonClick}
            data-testid="close-popup-button"
          >
            <CloseIcon />
          </CloseButton>
          {children}
        </ContentBox>
      </Contents>
    </DimmedPopupContainer>
  );
};

export default React.memo(DimmedPopup);
